import { useEffect, useState } from "react";
import { DollarSign, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { getMyBids } from "@/Calls/callSalvageAuth";

interface Bid {
    id: number; vehicle_vin: string | null; bid_amount: number;
    status: string; created_at: string; dealer_id: number | null;
}

const BID_STATUS: Record<string, string> = {
    pending:  "bg-yellow-100 text-yellow-700",
    accepted: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
};

export default function SalvageBidsPage() {
    const [bids,    setBids]    = useState<Bid[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter,  setFilter]  = useState("all");

    useEffect(() => {
        getMyBids()
            .then((d) => setBids(d.bids ?? []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    const filtered = filter === "all" ? bids : bids.filter((b) => b.status === filter);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold">My Bids</h1>
                    <p className="text-muted-foreground text-sm mt-0.5">{bids.length} total bids placed</p>
                </div>
                <div className="flex gap-2">
                    {["all", "pending", "accepted", "rejected"].map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilter(s)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                filter === s
                                    ? "bg-orange-600 text-white"
                                    : "border border-border text-muted-foreground hover:border-orange-400"
                            }`}
                        >
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-24">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            ) : filtered.length === 0 ? (
                <Card className="p-8 text-center text-muted-foreground">No bids found</Card>
            ) : (
                <Card>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="border-b">
                                <tr className="text-left">
                                    <th className="p-4 font-medium">Bid #</th>
                                    <th className="p-4 font-medium">VIN</th>
                                    <th className="p-4 font-medium">Amount</th>
                                    <th className="p-4 font-medium">Status</th>
                                    <th className="p-4 font-medium">Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((b) => (
                                    <tr key={b.id} className="border-b last:border-0 hover:bg-muted/30">
                                        <td className="p-4 text-muted-foreground">#{b.id}</td>
                                        <td className="p-4 font-mono text-xs">{b.vehicle_vin ?? "—"}</td>
                                        <td className="p-4 font-medium flex items-center gap-1">
                                            <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                                            {Number(b.bid_amount).toLocaleString()}
                                        </td>
                                        <td className="p-4">
                                            <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${BID_STATUS[b.status] ?? "bg-gray-100 text-gray-600"}`}>
                                                {b.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="p-4 text-muted-foreground">
                                            {new Date(b.created_at).toLocaleDateString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
        </div>
    );
}
